/**
 * Local calendar-day arithmetic, section 5.
 *
 * "Compute the difference in local calendar days, not UTC milliseconds - a naive UTC
 * subtraction shifts the day boundary for anyone not on UTC and was a live bug in the
 * prototype's weigh-in logging."
 *
 * Note `new Date('2026-08-31')` parses as UTC midnight, which is the exact trap. Every
 * ISO date here is parsed into a LOCAL date by components.
 */

export const DAYS_IN_PLAN = 112;          // 16 weeks
export const MAX_DAY_INDEX = DAYS_IN_PLAN - 1;

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

export class DateError extends Error {}

/** Parses YYYY-MM-DD as local midnight, never UTC. */
export function parseIsoLocal(iso: string): Date {
  const m = ISO.exec(iso);
  if (!m) throw new DateError(`date must be YYYY-MM-DD: ${iso}`);
  const [, y, mo, d] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  if (date.getFullYear() !== Number(y) || date.getMonth() !== Number(mo) - 1
      || date.getDate() !== Number(d)) {
    throw new DateError(`not a real calendar date: ${iso}`);
  }
  return date;
}

export function toIsoLocal(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Whole local calendar days from `from` to `to`. DST-safe: both ends are normalised
 *  to local midnight and the difference is rounded, so a 23- or 25-hour day still
 *  counts as one day. */
export function calendarDaysBetween(from: Date, to: Date): number {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** 0 = Monday, matching the plan's day-of-week convention (JS uses 0 = Sunday). */
export function mondayBasedDow(d: Date): number {
  return (d.getDay() + 6) % 7;
}

export function isMonday(iso: string): boolean {
  return mondayBasedDow(parseIsoLocal(iso)) === 0;
}

export interface DayPosition {
  /** Clamped to 0..111, which is what the app displays. */
  dayIndex: number;
  /** Unclamped, so "before the plan starts" and "after it ends" are distinguishable. */
  rawDay: number;
  week: number;           // 1..16
  dow: number;            // 0 = Monday
  notStarted: boolean;
  complete: boolean;      // past day 111: the plan is finished
  date: string;           // ISO date of the clamped dayIndex
}

export function positionFor(startDateIso: string, today: Date): DayPosition {
  const start = parseIsoLocal(startDateIso);
  const rawDay = calendarDaysBetween(start, today);
  const dayIndex = Math.min(Math.max(rawDay, 0), MAX_DAY_INDEX);
  return {
    dayIndex, rawDay,
    week: Math.floor(dayIndex / 7) + 1,
    dow: dayIndex % 7,
    notStarted: rawDay < 0,
    complete: rawDay > MAX_DAY_INDEX,
    date: toIsoLocal(addDays(start, dayIndex)),
  };
}

export function dateForDayIndex(startDateIso: string, dayIndex: number): string {
  return toIsoLocal(addDays(parseIsoLocal(startDateIso), dayIndex));
}
